import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col, Table } from 'react-bootstrap';
import { deliveryNoteService, clientService, productService } from '../../services/api';
import { Client, Product, DeliveryNote } from '../../types';
import { toast } from 'react-toastify';
import { PlusIcon, TrashIcon } from '../common/Icons';

interface DeliveryNoteFormProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  deliveryNote?: DeliveryNote | null;
}

interface DeliveryItem {
  product_id: number | null;
  product_name: string;
  quantity: number;
  unit_price: number;
}

const DeliveryNoteForm: React.FC<DeliveryNoteFormProps> = ({ show, onHide, onSuccess, deliveryNote }) => {
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [formData, setFormData] = useState({
    client_id: '',
    date: new Date().toISOString().split('T')[0],
    notes: '',
  });
  const [items, setItems] = useState<DeliveryItem[]>([]);

  useEffect(() => {
    if (show) {
      fetchClients();
      fetchProducts();
      if (deliveryNote) {
        setFormData({
          client_id: deliveryNote.client_id.toString(),
          date: deliveryNote.date,
          notes: deliveryNote.notes || '',
        });
        setItems(deliveryNote.items?.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: item.quantity,
          unit_price: item.unit_price,
        })) || []);
      }
    }
  }, [show, deliveryNote]);

  const fetchClients = async () => {
    try {
      const response = await clientService.getAll();
      setClients(response.data.data);
    } catch (error) {
      console.error('Erreur lors du chargement des clients');
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await productService.getAll();
      setProducts(response.data.data);
    } catch (error) {
      console.error('Erreur lors du chargement des produits');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const addItem = () => {
    setItems(prev => [...prev, {
      product_id: null,
      product_name: '',
      quantity: 1,
      unit_price: 0,
    }]);
  };

  const removeItem = (index: number) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, field: keyof DeliveryItem, value: any) => {
    setItems(prev => prev.map((item, i) => {
      if (i === index) {
        const updatedItem = { ...item, [field]: value };
        if (field === 'product_id' && value) {
          const product = products.find(p => p.id === parseInt(value));
          if (product) {
            updatedItem.product_name = product.name;
            updatedItem.unit_price = product.selling_price_ht;
          }
        }
        return updatedItem;
      }
      return item;
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error('Veuillez ajouter au moins un produit');
      return;
    }

    setLoading(true);
    try {
      const data = {
        ...formData,
        client_id: parseInt(formData.client_id),
        items: items.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
        })),
      };

      if (deliveryNote) {
        await deliveryNoteService.update(deliveryNote.id, data);
        toast.success('Bon de livraison modifié avec succès');
      } else {
        await deliveryNoteService.create(data);
        toast.success('Bon de livraison créé avec succès');
      }
      
      onSuccess();
      onHide();
      resetForm();
    } catch (error) {
      console.error(error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: '',
      date: new Date().toISOString().split('T')[0],
      notes: '',
    });
    setItems([]);
  };

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>{deliveryNote ? 'Modifier' : 'Nouveau'} bon de livraison</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Client *</Form.Label>
                <Form.Select
                  name="client_id"
                  value={formData.client_id}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Sélectionner un client</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Date *</Form.Label>
                <Form.Control
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6>Produits</h6>
              <Button variant="outline-primary" size="sm" onClick={addItem}>
                <PlusIcon className="me-1" />
                Ajouter un produit
              </Button>
            </div>
            
            {items.length > 0 && (
              <Table bordered>
                <thead>
                  <tr>
                    <th>Produit</th>
                    <th>Quantité</th>
                    <th>Prix unitaire</th>
                    <th>Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={index}>
                      <td>
                        <Form.Select
                          value={item.product_id || ''}
                          onChange={(e) => updateItem(index, 'product_id', parseInt(e.target.value))}
                          required
                        >
                          <option value="">Sélectionner un produit</option>
                          {products.map(product => (
                            <option key={product.id} value={product.id}>
                              {product.name}
                            </option>
                          ))}
                        </Form.Select>
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          step="0.01"
                          min="0.01"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value))}
                          required
                          style={{ width: '100px' }}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.unit_price}
                          onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value))}
                          required
                          style={{ width: '120px' }}
                        />
                      </td>
                      <td>{(item.quantity * item.unit_price).toFixed(2)} MAD</td>
                      <td>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeItem(index)}
                        >
                          <TrashIcon />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? 'Sauvegarde...' : (deliveryNote ? 'Modifier' : 'Créer')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default DeliveryNoteForm;